import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-3-3：兩位數 × 一位數（含進位）——三上初次教學版。
 * 與三下 math.mul.2x1 不同：三上重點是「第一次接觸進位」，難度偏低，
 * 且使用不同 kp id (`math.mul.2x1.review`) 以便獨立統計。
 *
 * difficulty 1：10–29 × 2–5（進位來自個位）
 * difficulty 2：20–59 × 3–7（進位多次）
 * difficulty 3：50–99 × 6–9（高位進位，難度同三下）
 */
export function generateMul2x1Up(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  let aMin: number, aMax: number, bMin: number, bMax: number;
  if (difficulty === 1) {
    [aMin, aMax, bMin, bMax] = [10, 29, 2, 5];
  } else if (difficulty === 2) {
    [aMin, aMax, bMin, bMax] = [20, 59, 3, 7];
  } else {
    [aMin, aMax, bMin, bMax] = [50, 99, 6, 9];
  }

  const a = randInt(rand, aMin, aMax);
  const b = randInt(rand, bMin, bMax);
  const correct = a * b;

  // Distractors modelling first-time carry mistakes
  const distractors = new Set<number>();

  // 1. Forgot carry: compute tens digit without carry from ones
  const onesProduct = (a % 10) * b;
  const carry = Math.floor(onesProduct / 10);
  const noCarry = Math.floor(a / 10) * b * 10 + (onesProduct % 10);
  if (noCarry !== correct && noCarry > 0) distractors.add(noCarry);

  // 2. Only ones digit computed (forgot tens entirely)
  const onesOnly = onesProduct;
  if (onesOnly !== correct && onesOnly > 0 && onesOnly < correct) distractors.add(onesOnly);

  // 3. Off-by-one carry (added 1 instead of actual carry)
  const offByOne = Math.floor(a / 10) * b * 10 + (onesProduct % 10) + 10;
  if (offByOne !== correct && offByOne > 0) distractors.add(offByOne);

  while (distractors.size < 3) {
    const offset = randInt(rand, 1, 20) * (rand() > 0.5 ? 1 : -1);
    const candidate = correct + (offset === 0 ? 7 : offset);
    if (candidate > 0 && candidate !== correct && !distractors.has(candidate)) {
      distractors.add(candidate);
    }
  }

  const optionList = [correct, ...Array.from(distractors).slice(0, 3)];
  const shuffled = [...optionList].sort((x, y) => mulberry32(seed + x)() - mulberry32(seed + y)());

  return {
    kind: 'mcq',
    prompt: `${a} × ${b} = ?`,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation: `個位 ${a % 10}×${b}=${onesProduct}${carry > 0 ? `（進位 ${carry}）` : ''}；十位 ${Math.floor(a / 10)}×${b}${carry > 0 ? `+${carry}` : ''}=${Math.floor(a / 10) * b + carry} → ${correct}`,
    kp: 'math.mul.2x1.review',
  };
}
